import React from 'react';
import { Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { ICON_ALIASES } from '_constants/icons';
import { MODAL_KEYS } from '_constants/modals';
import { theme } from '_constants/theme';
import { selectBankAccountItemById, selectDeleting } from '_store/bankAccounts/selector';
import { deleteAccount } from '_store/bankAccounts/slice';
import { selectModalDataByKey, selectModalVisibleByKey } from '_store/modals/selector';
import { hideModal } from '_store/modals/slice';
import { selectActiveMainProductId } from '_store/order/selector';
import { selectCustomerId } from '_store/user/selector';

import Button from 'components/atomic/Button';
import Icon from 'components/atomic/Icon';
import Spinner from 'components/atomic/Spinner';
import Text from 'components/atomic/layouts/Text';

const StyledModalBody = styled(Modal.Body)`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${theme.spaces.large2X}px;
`;

const StyledCloseActionContainer = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  top: ${-theme.spaces.large3X}px;
  right: 0;
  font-family: 'Inter';
  font-style: normal;
  font-size: ${theme.fontSizes.regular}px;
  font-weight: 400;
  color: ${theme.colors.white};
  cursor: pointer;
`;

const StyledIconContainer = styled.div`
  margin-right: ${theme.spaces.small2X}px;
`;

const StyledConfirmActionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spaces.smallX}px;
  margin-top: ${theme.spaces.small - 2}px;
`;

const StyledSpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const DeleteBankAccountModal = () => {
  const dispatch = useDispatch();
  const visible = useSelector(selectModalVisibleByKey(MODAL_KEYS.bankAccountDelete));
  const { accountId } = useSelector(selectModalDataByKey(MODAL_KEYS.bankAccountDelete));
  const { title, text } = useSelector(selectBankAccountItemById(accountId)) || {};
  const deleting = useSelector(selectDeleting);
  const customerId = useSelector(selectCustomerId);
  const productId = useSelector(selectActiveMainProductId);

  const handleClose = () => {
    dispatch(hideModal({ key: MODAL_KEYS.bankAccountDelete }));
  };

  const handleConfirm = () => {
    dispatch(deleteAccount({ customerId, productId, accountId }));
  };

  return (
    <Modal
      dialogClassName="statement-delete-confirmation-modal"
      size="sm"
      show={visible}
      onHide={handleClose}
      backdrop="static"
      keyboard={false}
      centered
    >
      <StyledModalBody>
        <StyledCloseActionContainer onClick={handleClose}>
          <StyledIconContainer>
            <Icon alias={ICON_ALIASES.close} color={theme.colors.white} />
          </StyledIconContainer>
          Close
        </StyledCloseActionContainer>
        <Text
          size={theme.fontSizes.largeX}
          weight={700}
          color={theme.colors.gray.primary}
          bottomSpace={theme.spaces.medium}
        >
          Are you sure?
        </Text>
        <Text size={theme.fontSizes.small} color={theme.colors.gray.secondary} bottomSpace={theme.spaces.medium}>
          You are about to delete an account:
        </Text>
        <Text
          size={theme.fontSizes.small}
          weight={700}
          color={theme.colors.gray.secondary}
          bottomSpace={theme.spaces.medium}
        >
          {title}
        </Text>
        <Text size={theme.fontSizes.small} color={theme.colors.gray.secondary} bottomSpace={theme.spaces.medium}>
          {text}
        </Text>
        <StyledConfirmActionContainer>
          <Button color={theme.colors.gray.secondary} disabled={deleting} onClick={handleClose}>
            Cancel
          </Button>
          <Button color={theme.colors.indigo.primary} disabled={deleting} onClick={handleConfirm}>
            Delete
          </Button>
          {deleting && (
            <StyledSpinnerContainer>
              <Spinner color={theme.colors.indigo.superLight} />
            </StyledSpinnerContainer>
          )}
        </StyledConfirmActionContainer>
      </StyledModalBody>
    </Modal>
  );
};

export default DeleteBankAccountModal;
